/* ═══════════════════════════════════════════════════════════════
   Collab state — the PURE decisions behind live shared drafts
   (docs/SPEC_WORKFLOW.md §5). No Yjs, no socket, no React: everything
   here is a function of its arguments so it is unit-tested directly
   (collabState.test.ts). The server half lives in ec2-api/collab-rules.js.
   ═══════════════════════════════════════════════════════════════ */

/** The provider's connection, in plain words. */
export type CollabStatus = 'connecting' | 'connected' | 'offline';

/** The one save chip: Saved / Saving… / Offline (DESIGN §6 — plain words). */
export type SaveState = 'saved' | 'saving' | 'offline';

/** Close code the server sends when the draft row is deleted (discard or
 *  publish) — ec2-api/collab-rules.js CLOSE.DISCARDED. */
export const CLOSE_DRAFT_DISCARDED = 4409;

/** Close code the server sends when the row's JSON was written outside the
 *  editor (ydoc_state reset) - ec2-api/collab-rules.js CLOSE.DOCUMENT_RESET. */
export const CLOSE_DOCUMENT_RESET = 4410;

/** How many peers show as chips before the rest fold into "+N". */
export const MAX_PEER_CHIPS = 5;

/** Cursor palette size — `--collab-cursor-1 … -6` in base.css. */
export const CURSOR_SLOTS = 6;

export type CollabDocKind = 'master' | 'project' | 'module' | 'forma';

export function collabDocName(kind: CollabDocKind, id: string): string {
  return `${kind}:${id}`;
}

/** The deterministic id of the shared draft row. MUST match
 *  `draftIdFor` in ec2-api/collab-rules.js (tests/ec2-collab-rules.test.ts
 *  asserts they agree) so the client's first mirror write and the server's
 *  first store land on ONE row. */
export function sharedDraftId(kind: CollabDocKind, id: string): string {
  return kind === 'project' ? `pdraft-${id}` : `draft-${id}`;
}

/** Chip state. Offline wins (edits are queued locally, not saved); then any
 *  write still in flight — unsent Yjs updates or the JSON mirror — is
 *  "Saving…"; otherwise "Saved". Before the first connection resolves the
 *  chip says Saving… rather than claiming a save that has not happened. */
export function deriveSaveState(input: {
  status: CollabStatus;
  unsyncedChanges: number;
  mirrorPending: boolean;
}): SaveState {
  if (input.status === 'offline') return 'offline';
  if (input.status === 'connecting') return 'saving';
  if (input.unsyncedChanges > 0 || input.mirrorPending) return 'saving';
  return 'saved';
}

export function saveStateLabel(state: SaveState): string {
  return state === 'saving' ? 'Saving…' : state === 'offline' ? 'Offline' : 'Saved';
}

/** The Spec Studio's chip adds ONE state to the shared save chip:
 *  "Connecting…" — the socket is opening, or it is open but the document
 *  has not synced and been seeded yet. Nothing can be saving then (the
 *  canvas is not bound), so claiming "Saving…" would be a lie. */
export type StudioChipState = SaveState | 'connecting';

export function deriveStudioChipState(input: {
  status: CollabStatus;
  unsyncedChanges: number;
  mirrorPending: boolean;
  /** The shared document has synced AND been seeded (the canvas is bound). */
  ready: boolean;
}): StudioChipState {
  if (input.status === 'offline') return 'offline';
  if (input.status === 'connecting' || !input.ready) return 'connecting';
  return deriveSaveState(input);
}

export function studioChipLabel(state: StudioChipState): string {
  return state === 'connecting' ? 'Connecting…' : saveStateLabel(state);
}

/** Why live editing is unavailable — a close the server will repeat on every
 *  retry (4001 auth, 4003 origin, 4400 bad request, 4429 too many — the
 *  CLOSE codes in ec2-api/collab-rules.js), or a per-document refusal. */
export type LiveBlock = 'auth' | 'origin' | 'bad-request' | 'too-many' | 'denied';

/** A close code that no reconnect can fix, or null (transient / handled
 *  elsewhere: 4409 discard and 4410 reset have their own flows). */
export function terminalCloseBlock(code: number | null): LiveBlock | null {
  switch (code) {
    case 4001:
      return 'auth';
    case 4003:
      return 'origin';
    case 4400:
      return 'bad-request';
    case 4429:
      return 'too-many';
    default:
      return null;
  }
}

/** The plain reason, used by the toast AND the Edit button's tooltip. */
export function liveBlockReason(block: LiveBlock): string {
  switch (block) {
    case 'origin':
      return 'this app version cannot reach the collaboration server';
    case 'auth':
      return 'sign in again';
    case 'too-many':
      return 'too many editors connected';
    case 'denied':
      return 'you do not have access to edit this master';
    case 'bad-request':
      return 'the collaboration server could not open this master';
  }
}

export function liveBlockMessage(block: LiveBlock): string {
  return `Live editing unavailable: ${liveBlockReason(block)}`;
}

/** The block after an event: a terminal close sets it, the first full sync
 *  (the server accepted us) clears it, anything else leaves it alone — a
 *  socket that merely OPENS before the server closes it is not a success. */
export function nextLiveBlock(
  current: LiveBlock | null,
  event: { kind: 'close'; code: number | null } | { kind: 'synced' } | { kind: 'denied' }
): LiveBlock | null {
  if (event.kind === 'synced') return null;
  if (event.kind === 'denied') return 'denied';
  return terminalCloseBlock(event.code) ?? current;
}

/** The master status chip. Edit is always the draft. View reads the
 *  master's own status — a master that was never published reads "DRAFT"
 *  (as the library does), never "PUBLISHED". */
export function masterStatusChip(status: string | null | undefined, editing: boolean): string {
  if (editing) return 'DRAFT';
  switch (status) {
    case 'published':
    case 'active':
      return 'PUBLISHED';
    case 'pending_review':
      return 'PENDING REVIEW';
    case 'archived':
      return 'ARCHIVED';
    default:
      return 'DRAFT';
  }
}

/** Seed the shared document from the JSON draft / published master? Only the
 *  ONE writer the server named (`seeder`), only after the first sync (an
 *  unsynced doc always looks empty), only into an empty, never-seeded doc. */
export function shouldSeed(input: {
  synced: boolean;
  readOnly: boolean;
  fragmentEmpty: boolean;
  seeded: boolean;
  seeder: string | null;
  mySeat: string;
}): boolean {
  if (!input.synced || input.readOnly) return false;
  if (input.seeded || !input.fragmentEmpty) return false;
  return input.seeder === input.mySeat;
}

/** A stable palette slot for a person, so the same name wears the same
 *  colour on every screen. 1-based, matching the CSS tokens. */
export function cursorSlot(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return (h % CURSOR_SLOTS) + 1;
}

export interface CollabPeer {
  name: string;
  /** 1-based palette slot → `--collab-cursor-<slot>`. */
  slot: number;
}

/** Awareness states → the people here besides me, one chip per NAME (the
 *  same person in two windows is one person). Order: first seen. */
export function peersFromAwareness(
  states: Iterable<[number, unknown]>,
  myClientId: number,
  myName: string
): CollabPeer[] {
  const seen = new Set<string>([myName]);
  const out: CollabPeer[] = [];
  for (const [clientId, state] of states) {
    if (clientId === myClientId) continue;
    const user = (state as { user?: { name?: unknown; slot?: unknown } } | null)?.user;
    const name = typeof user?.name === 'string' ? user.name.trim() : '';
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const slot =
      typeof user?.slot === 'number' && user.slot >= 1 && user.slot <= CURSOR_SLOTS
        ? user.slot
        : cursorSlot(name);
    out.push({ name, slot });
  }
  return out;
}

/** ≤ MAX_PEER_CHIPS chips, then "+N". */
export function summarizePeers(
  peers: CollabPeer[],
  max: number = MAX_PEER_CHIPS
): { shown: CollabPeer[]; extra: number } {
  if (peers.length <= max) return { shown: peers, extra: 0 };
  return { shown: peers.slice(0, max), extra: peers.length - max };
}

/** Initials for a peer chip: first letters of the first two words. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0] ?? '');
  return letters.join('').toUpperCase() || '?';
}

/** Order-stable layer storage: the Y.Map holds `{ pos, layer }` per layer id
 *  (a map has no order of its own). Sorted by pos, ties by id. */
export interface StoredLayer<L> {
  pos: number;
  layer: L;
}

export function orderStoredLayers<L>(entries: Iterable<[string, StoredLayer<L>]>): L[] {
  return [...entries]
    .filter(([, v]) => v && typeof v === 'object' && 'layer' in v)
    .sort((a, b) => a[1].pos - b[1].pos || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([, v]) => v.layer);
}

/** The writes that turn the stored layer map into `next`: ids to delete and
 *  entries to set (only the ones that actually changed). */
export function diffStoredLayers<L extends { id: string }>(
  current: Map<string, StoredLayer<L>>,
  next: L[]
): { remove: string[]; set: Array<[string, StoredLayer<L>]> } {
  const keep = new Set(next.map((l) => l.id));
  const remove = [...current.keys()].filter((id) => !keep.has(id));
  const set: Array<[string, StoredLayer<L>]> = [];
  next.forEach((layer, pos) => {
    const prev = current.get(layer.id);
    const entry: StoredLayer<L> = { pos, layer };
    if (!prev || prev.pos !== pos || JSON.stringify(prev.layer) !== JSON.stringify(layer)) {
      set.push([layer.id, entry]);
    }
  });
  return { remove, set };
}

/** May the JSON mirror write the draft row right now? Never once the draft
 *  has ENDED — a teammate's discard/publish (the 4409 close), or this
 *  window's own discard/publish — or a late debounced write would recreate
 *  the row and resurrect a draft everyone was told is gone. */
export function mirrorWriteAllowed(input: { ended: boolean; docDiscarded: boolean }): boolean {
  return !input.ended && !input.docDiscarded;
}

/** Why the Studio is re-reading the master after its shared document closed.
 *  'ended-elsewhere' = a teammate published / discarded (4409, or a 409
 *  draft-ended on a mirror write); 'reset' = the row changed outside the
 *  editor (4410); 'own-publish' = this window published and already holds
 *  the new master. Ended elsewhere and reset MUST re-read from the server
 *  (the cache still holds the old version and the deleted draft row), and
 *  until a new draft row / live doc appears there is no draft. */
export type DraftReloadCause = 'ended-elsewhere' | 'reset' | 'own-publish';

export function draftReloadPlan(cause: DraftReloadCause): {
  /** Drop the cached master + drafts before reading. */
  refetch: boolean;
  /** The mode the Studio lands in. */
  mode: 'view' | 'keep';
  /** The draft state until the re-read lands. */
  draftExists: false;
} {
  return {
    refetch: cause !== 'own-publish',
    mode: cause === 'ended-elsewhere' ? 'view' : 'keep',
    draftExists: false,
  };
}

/** Is there a shared draft right now? Never once the draft ENDED (the doc
 *  was evicted / the server said draft-ended) — only a fresh row or a fresh
 *  live doc's own edit brings it back. */
export function draftExistsNow(input: {
  ended: boolean;
  draftRow: boolean;
  edited: boolean;
}): boolean {
  return !input.ended && (input.draftRow || input.edited);
}

/** Show View mode's "Draft in progress" line? Only for a draft row that
 *  exists or a live doc with people in it — never "nobody here now" for a
 *  draft that is gone. */
export function showDraftInProgress(input: {
  ended: boolean;
  draftRow: boolean;
  peerCount: number;
}): boolean {
  return !input.ended && (input.draftRow || input.peerCount > 0);
}

/** The "Draft in progress — …" line in View mode: who is in it now. */
export function draftPresenceLabel(peers: Array<{ name: string }>): string {
  if (!peers.length) return 'nobody here now';
  if (peers.length === 1) return peers[0].name;
  const others = peers.length - 1;
  return `${peers[0].name}, ${others} other${others === 1 ? '' : 's'}`;
}

/** The Discard confirm's body: who loses the draft. */
export function discardConfirmBody(peers: Array<{ name: string }>): string {
  if (!peers.length) return 'Discard this draft for everyone? Nobody else is editing it right now.';
  const n = peers.length - 1;
  const who =
    n === 0 ? `${peers[0].name} is` : `${peers[0].name} and ${n} other${n === 1 ? '' : 's'} are`;
  return `Discard this draft for everyone? ${who} editing it. This cannot be undone.`;
}

/** An answered offer (the server already rewrote the PUBLISHED master's
 *  layer) lands on two copies, each transformed INDEPENDENTLY: the published
 *  layers (what View shows) and the draft's own shared layers (so the next
 *  publish cannot write the old layer back). The published copy is never
 *  written into the draft wholesale. The draft write counts as an edit only
 *  when a draft exists — answering never starts one. */
export function layerDecisionTargets<L extends { id: string }>(input: {
  publishedLayers: L[];
  draftLayers: L[];
  hasDraft: boolean;
  layerId: string;
  apply: (layer: L) => L;
}): { published: L[]; draft: L[]; draftWrite: 'edit' | 'sync' } {
  const { publishedLayers, draftLayers, hasDraft, layerId, apply } = input;
  const map = (list: L[]) => list.map((l) => (l.id === layerId ? apply(l) : l));
  return {
    published: map(publishedLayers),
    draft: map(draftLayers),
    draftWrite: hasDraft ? 'edit' : 'sync',
  };
}

/** The server refused a mirror write because the draft ENDED (409
 *  draft-ended: published / discarded, raced the eviction). Treated exactly
 *  like a 4409: stop mirroring, no error toast, refresh. */
export function isDraftEndedError(err: unknown): boolean {
  const e = err as { status?: unknown; message?: unknown } | null;
  const msg = typeof e?.message === 'string' ? e.message : '';
  return /\bdraft-ended\b/.test(msg);
}

/** The project-spec Publish confirm's body: what it does, and who else is in
 *  the draft right now (SYNC D12). */
export function publishConfirmBody(peers: Array<{ name: string }>): string {
  const lead = 'Publish this section? Exports and issues will use this version from now on.';
  if (!peers.length) return `${lead} Nobody else is editing it right now.`;
  const n = peers.length - 1;
  const who =
    n === 0 ? `${peers[0].name} is` : `${peers[0].name} and ${n} other${n === 1 ? '' : 's'} are`;
  return `${lead} ${who} also in this draft — their edits so far are included.`;
}
